import { ClearOutlined } from '@ant-design/icons';
import { Button } from 'antd';
import cn from 'classnames';
import { useMemo } from 'react';

import { CheckboxFilter, TreeCheckboxFilter } from '@app/components';
import {
  LEADS_SALES_FUNNEL_CLEARED_SELECTION,
  normalizeLeadsSalesFunnelSelection,
  resolveLeadsSalesFunnelSelection,
} from '@app/pages/Leads/model/salesFunnelSelection';
import { MultiSelect } from '@app/shared/components';
import type { MultiSelectValue } from '@app/shared/components/MultiSelect/types';

import { useLeadsRegistryFilterData } from '../../../../hooks';
import { useLeadsRegistryScopedFilters } from '../../../../hooks/useLeadsRegistryFilters';
import { LeadsRegistryDateFilter } from '../../LeadsRegistryDateFilter';

import type {
  LeadsRegistryCheckboxFilterKey,
  LeadsRegistryFilterScope,
} from '../../../../model/types';
import type { Key, FC } from 'react';

import './LeadsRegistryFiltersBar.scss';

// Scoped class applied to every portaled filter overlay so the accordion
// responsive styles (full-width popovers on <= 800px) can target them without
// touching the shared filter components globally.
const FILTER_POPUP_CLASS = 'LeadsRegistryFiltersBar__filter-popup';

// Renders popups inside the FiltersAccordion container so their width naturally
// matches it — instead of portaling to body where sidebar width is unknown.
const getFiltersPopupContainer = (trigger: HTMLElement): HTMLElement =>
  trigger.closest<HTMLElement>('.LeadsRegistryHeader__filters') ??
  document.body;

interface CheckboxFilterConfig {
  key: LeadsRegistryCheckboxFilterKey;
  title: string;
  dataTestId: string;
}

const CHECKBOX_FILTERS: CheckboxFilterConfig[] = [
  { key: 'branches', title: 'Филиалы', dataTestId: 'branches-filter' },
  { key: 'channels', title: 'Каналы', dataTestId: 'channels-filter' },
  {
    key: 'integrations',
    title: 'Интеграции',
    dataTestId: 'integrations-filter',
  },
  { key: 'users', title: 'Ответственный', dataTestId: 'responsible-filter' },
  { key: 'statuses', title: 'Статус', dataTestId: 'status-filter' },
  { key: 'crmTaskStatuses', title: 'Задачи', dataTestId: 'tasks-filter' },
];

const getSelectedTagCount = (tags: MultiSelectValue): number => {
  const selectedTags = new Set<string>();

  tags.groups.forEach((group) => {
    group.selectedValues.forEach((value) => {
      selectedTags.add(value);
    });
  });

  return selectedTags.size;
};

interface LeadsRegistryFiltersBarProps {
  filterScope?: LeadsRegistryFilterScope;
}

export const LeadsRegistryFiltersBar: FC<LeadsRegistryFiltersBarProps> = ({
  filterScope = 'registry',
}) => {
  const {
    filters,
    isFiltersResetDisabled,
    onCheckboxFilterChange,
    onTagsChange,
    onFunnelStagesChange,
    onSalesFunnelChange: updateSalesFunnel,
    onResetFilters,
  } = useLeadsRegistryScopedFilters(filterScope);
  const {
    checkboxOptions,
    tagOptions,
    funnelTreeData,
    funnels,
    isFilterDataLoading,
  } = useLeadsRegistryFilterData();

  const selectedTagCount = getSelectedTagCount(filters.tags);
  const effectiveFunnelSelection = useMemo(
    () =>
      filterScope === 'funnels'
        ? resolveLeadsSalesFunnelSelection(funnels, {
            salesFunnelId: filters.salesFunnelId,
            funnelStages: filters.funnelStages,
          })
        : {
            salesFunnelId: filters.salesFunnelId,
            funnelStages: filters.funnelStages,
          },
    [filterScope, filters.funnelStages, filters.salesFunnelId, funnels]
  );

  const salesFunnelChangeHandler = (
    salesFunnelId: number | null,
    funnelStages: number[]
  ) => {
    const nextSelection =
      filterScope === 'funnels'
        ? normalizeLeadsSalesFunnelSelection(funnels, {
            salesFunnelId,
            funnelStages,
          })
        : { salesFunnelId, funnelStages };

    updateSalesFunnel(nextSelection.salesFunnelId, nextSelection.funnelStages);
  };

  const funnelStagesChangeHandler = (nextLeafKeys: Key[]) => {
    const nextStageIds = nextLeafKeys.map(Number);

    if (filterScope !== 'funnels') {
      onFunnelStagesChange(nextStageIds);
      return;
    }

    // Unchecking every stage is an explicit "no funnel" choice: it must not
    // collapse into the `null + []` default marker, which would silently
    // re-select the first funnel and make the clear look like a no-op.
    if (nextStageIds.length === 0) {
      salesFunnelChangeHandler(
        LEADS_SALES_FUNNEL_CLEARED_SELECTION.salesFunnelId,
        LEADS_SALES_FUNNEL_CLEARED_SELECTION.funnelStages
      );
      return;
    }

    const selectedFunnelIds = new Set(
      funnels
        .filter((funnel) =>
          nextStageIds.some((stageId) =>
            (funnel.Stages ?? []).some((stage) => stage.Id === stageId)
          )
        )
        .map((funnel) => funnel.Id)
    );

    salesFunnelChangeHandler(
      selectedFunnelIds.size === 1 ? [...selectedFunnelIds][0] : null,
      nextStageIds
    );
  };

  return (
    <div className="LeadsRegistryFiltersBar">
      <TreeCheckboxFilter
        title="Воронки"
        dataTestId="funnels-filter"
        disabled={isFilterDataLoading}
        treeData={funnelTreeData}
        value={effectiveFunnelSelection.funnelStages}
        onChange={funnelStagesChangeHandler}
        isMultipleSelect={filterScope !== 'funnels'}
        popoverRootClassName={FILTER_POPUP_CLASS}
        getPopupContainer={getFiltersPopupContainer}
      />

      {CHECKBOX_FILTERS.map(({ key, title, dataTestId }) => (
        <CheckboxFilter
          key={key}
          title={title}
          dataTestId={dataTestId}
          disabled={isFilterDataLoading}
          options={checkboxOptions[key]}
          checkedValues={filters[key]}
          onChange={(values) => onCheckboxFilterChange(key, values)}
          popoverRootClassName={FILTER_POPUP_CLASS}
          getPopupContainer={getFiltersPopupContainer}
        />
      ))}

      <MultiSelect
        className={cn('LeadsRegistryFiltersBar__tags', {
          'LeadsRegistryFiltersBar__tags--active': selectedTagCount > 0,
        })}
        classNames={{
          popup: {
            root: `LeadsRegistryFiltersBar__tags-popup ${FILTER_POPUP_CLASS}`,
          },
        }}
        size="small"
        allowClear
        disabled={isFilterDataLoading}
        options={tagOptions}
        value={filters.tags}
        onChange={onTagsChange}
        fixedInputLabel="Теги"
        placeholder="Выберите теги"
        dataTestId="tags-filter"
      />

      <LeadsRegistryDateFilter filterScope={filterScope} />

      {!isFiltersResetDisabled && (
        <Button
          className="LeadsRegistryFiltersBar__reset"
          size="small"
          icon={<ClearOutlined />}
          htmlType="button"
          onClick={onResetFilters}
        >
          Сброс
        </Button>
      )}
    </div>
  );
};
